import { useEffect, useState } from "react";
import { getNotesByInteractionId } from "@/repository/queryFns/history";
import { Note } from "@/components/organisms/history/types";
import { InteractionActionPopoverType } from "@/common/types/transfer";
import { ServerEvent } from "@/common/notification";
import { notificationListener } from "@/hooks/notification";
import {
	TextSummaryReadyNotification,
	TextSummaryStatus,
} from "@/repository/models";

export interface NotesHookData {
	getNotes: (callback: (notes: Note[]) => void) => void;
	onClickNotePopover: (notes: Note[]) => void;
}

export const useNotes = (
	selectedInteractionId: string | undefined,
	setNotes: React.Dispatch<React.SetStateAction<Note[]>>,
	togglePopover: (type: InteractionActionPopoverType) => void
): NotesHookData => {
	const [interactionId, setInteractionId] = useState<string>("");

	useEffect(() => {
		if (!selectedInteractionId) {
			return;
		}

		if (selectedInteractionId !== interactionId) {
			setInteractionId(selectedInteractionId);
		}
	}, [selectedInteractionId]);

	useEffect(() => {
		if (selectedInteractionId && interactionId !== "") {
			getNotes(initialNotePopover);
		}
	}, [interactionId]);

	const getNotes = (callback: (notes: Note[]) => void) => {
		if (selectedInteractionId) {
			getNotesByInteractionId(selectedInteractionId).then(callback);
		}
	};

	const initialNotePopover = (notes: Note[]) => {
		if (notes.length > 0) {
			setNotes(notes);
		} else {
			setNotes([]);
		}
	};

	const onClickNotePopover = (notes: Note[]) => {
		if (notes.length > 0) {
			setNotes(notes);
		}

		togglePopover(InteractionActionPopoverType.InteractionNote);
	};

	// When the background close-summary finishes it is auto-inserted as a note server-side. Refresh the
	// list so it appears in an already-open popover without the agent having to reopen it.
	notificationListener<TextSummaryReadyNotification>(
		ServerEvent.TextSummaryReady,
		(data: TextSummaryReadyNotification) => {
			if (
				data.status === TextSummaryStatus.Generated &&
				data.sessionId === selectedInteractionId
			) {
				getNotes(initialNotePopover);
			}
		}
	);

	return { getNotes: getNotes, onClickNotePopover: onClickNotePopover };
};
